import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { ExtendedUser, UserProfile, safeCastPlan } from '@/types/user';
import { toast } from 'sonner';
import { getStoredFingerprint } from '@/utils/deviceFingerprint';
interface AuthContextType {
  user: ExtendedUser | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, name: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  updateUser: (updates: Partial<ExtendedUser>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching profile:', error);
        return null;
      }

      if (!data) {
        // Create profile asynchronously without blocking
        setTimeout(async () => {
          await supabase
            .from('profiles')
            .insert({
              user_id: userId,
              monthly_images_used: 0,
              monthly_scripts_used: 0,
              monthly_audio_minutes_used: 0,
              images_limit: 5,
              scripts_limit: 5,
              audio_minutes_limit: 5,
              plan: 'free',
              full_name: user?.email?.split('@')[0] || 'User'
            });
        }, 0);

        return {
          id: '',
          user_id: userId,
          full_name: null,
          avatar_url: null,
          plan: 'free',
          credits_used: 0,
          credits_limit: 5,
          business_name: null,
          referral_code: null
        };
      }

      return {
        id: data.id,
        user_id: data.user_id,
        full_name: data.full_name,
        avatar_url: data.avatar_url,
        plan: safeCastPlan(data.plan),
        credits_used: (data.monthly_images_used + data.monthly_scripts_used + data.monthly_audio_minutes_used),
        credits_limit: safeCastPlan(data.plan) === 'free' 
          ? 5 
          : (data.images_limit + data.scripts_limit + data.audio_minutes_limit),
        business_name: data.business_name,
        referral_code: data.referral_code
      };
    } catch (error) {
      console.error('Error in fetchUserProfile:', error);
      return null;
    }
  };

  const createExtendedUser = async (supabaseUser: User): Promise<ExtendedUser> => {
    // Create basic user first for immediate UI update
    const basicUser = {
      ...supabaseUser,
      name: supabaseUser.user_metadata?.full_name || supabaseUser.email?.split('@')[0] || 'User',
      avatarUrl: undefined,
      plan: 'free' as const,
      creditsUsed: 0,
      creditsLimit: 5,
      isAdmin: false,
      businessName: undefined,
      referralCode: undefined
    };

    // Fetch profile asynchronously
    setTimeout(async () => {
      const profile = await fetchUserProfile(supabaseUser.id);
      if (profile) {
        setUser(prev => prev ? {
          ...prev,
          name: profile.full_name || prev.name,
          avatarUrl: profile.avatar_url || undefined,
          plan: profile.plan,
          creditsUsed: profile.credits_used,
          creditsLimit: profile.credits_limit,
          businessName: profile.business_name || undefined,
          referralCode: profile.referral_code || undefined
        } : null);
      }
    }, 0);

    return basicUser;
  };

  const updateUser = (updates: Partial<ExtendedUser>) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  };

  useEffect(() => {
    let mounted = true;
    
    const initializeAuth = async () => {
      try {
        // Get initial session quickly
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        
        if (initialSession?.user && mounted) {
          const extendedUser = await createExtendedUser(initialSession.user);
          setSession(initialSession);
          setUser(extendedUser);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      
      try {
        setSession(session);
        
        if (session?.user) {
          const extendedUser = await createExtendedUser(session.user);
          setUser(extendedUser);
          
          // Show welcome message for new signups
          if (event === 'SIGNED_IN' && session.user.created_at) {
            const userCreatedTime = new Date(session.user.created_at).getTime();
            const currentTime = new Date().getTime();
            const fiveMinutesInMs = 5 * 60 * 1000;
            
            if (currentTime - userCreatedTime < fiveMinutesInMs) {
              setTimeout(() => {
                toast.success('Welcome to Genova AI Studio! 🎉', {
                  description: 'Your account has been created successfully!'
                });
              }, 500);
            } else {
              setTimeout(() => {
                toast.success(`Welcome back, ${extendedUser.name}! 👋`);
              }, 300);
            }
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        setUser(null);
      }
      
      setLoading(false);
    });

    // Initialize auth
    initializeAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password
      });

      if (error) {
        console.error('Sign in error:', error);
        let errorMessage = 'An error occurred during sign in';
        
        if (error.message.includes('Invalid login credentials')) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = 'Please check your email and confirm your account before signing in.';
        } else if (error.message.includes('Too many requests')) {
          errorMessage = 'Too many sign in attempts. Please wait a moment and try again.';
        } else {
          errorMessage = error.message;
        }
        
        toast.error(errorMessage);
        return { error };
      }

      return { error: null };
    } catch (error: any) {
      console.error('Unexpected sign in error:', error);
      toast.error('An unexpected error occurred. Please try again.');
      return { error };
    }
  };

  const signUp = async (email: string, password: string, name: string) => {
    try {
      // Check device limit before signup
      const deviceFingerprint = getStoredFingerprint();
      
      const { data: canCreate, error: checkError } = await supabase
        .rpc('can_device_create_account', { p_fingerprint: deviceFingerprint });
      
      if (checkError) {
        console.error('Error checking device limit:', checkError);
      } else if (!canCreate) {
        toast.error('Device limit reached. You can only create 3 accounts per device.');
        return { error: { message: 'Device limit reached' } };
      }
      
      const redirectUrl = `${window.location.origin}/dashboard`;
      
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password: password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: name.trim()
          }
        }
      });

      if (error) {
        console.error('Sign up error:', error);
        let errorMessage = 'An error occurred during sign up';
        
        if (error.message.includes('User already registered')) {
          errorMessage = 'An account with this email already exists. Please try signing in instead.';
        } else if (error.message.includes('Password should be at least')) {
          errorMessage = 'Password must be at least 6 characters long.';
        } else if (error.message.includes('Invalid email')) {
          errorMessage = 'Please enter a valid email address.';
        } else {
          errorMessage = error.message;
        }
        
        toast.error(errorMessage);
        return { error };
      }

      // Register device with new account
      if (data.user) {
        await supabase.rpc('register_device_account', {
          p_fingerprint: deviceFingerprint,
          p_user_id: data.user.id,
          p_ip: null,
          p_user_agent: navigator.userAgent
        });
      }

      if (data.user && !data.session) {
        toast.success('Account created! Please check your email to confirm your account.');
      } else {
        toast.success('Account created successfully! Welcome to Genova AI Studio! 🎉');
      }
      
      return { error: null };
    } catch (error: any) {
      console.error('Unexpected sign up error:', error);
      toast.error('An unexpected error occurred. Please try again.');
      return { error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Sign out error:', error);
        toast.error('Error signing out. Please try again.');
      } else {
        toast.success('Successfully signed out!');
      }
    } catch (error: any) {
      console.error('Unexpected sign out error:', error);
      toast.error('An unexpected error occurred while signing out.');
    }
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
